import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { CommunicationTemplateService, CommunicationTemplate } from '../services/communication-template.service';
import { TemplateEditorComponent } from './template-editor/template-editor.component';
import { AuthService, UserRole } from '../auth/auth.service';
import { PermissionService } from '../shared/services/permission.service';
import { RCOversightService } from '../services/rc-oversight.service';
import { ErrorService } from '../shared/services/error.service';
import { RCDataSyncService } from '../services/rc-data-sync.service';
import { Subscription } from 'rxjs';
import { config } from '../config';

@Component({
  selector: 'app-communication-templates',
  standalone: true,
  imports: [CommonModule, FormsModule, TemplateEditorComponent],
  templateUrl: './communication-templates.component.html',
  styleUrls: ['./communication-templates.component.css']
})
export class CommunicationTemplatesComponent implements OnInit, OnDestroy {
  templates: CommunicationTemplate[] = [];
  filteredTemplates: CommunicationTemplate[] = [];
  searchQuery: string = '';
  selectedChannel: string = 'all';
  isLoading: boolean = false;
  showModal: boolean = false;
  selectedTemplate: CommunicationTemplate | null = null;
  isEditMode: boolean = false;
  copiedTemplateId: string | null = null;
  showDeleteModal: boolean = false;
  templateToDelete: CommunicationTemplate | null = null;
  private rcDataSyncSubscription: Subscription | null = null;

  channels = [
    { value: 'all', label: 'All channels' },
    { value: 'email', label: 'Email' },
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' }
  ];

  constructor(
    private communicationTemplateService: CommunicationTemplateService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private rcOversightService: RCOversightService,
    private http: HttpClient,
    private errorService: ErrorService,
    private rcDataSyncService: RCDataSyncService
  ) {}

  ngOnInit() {
    this.loadTemplates();
    this.initializeRCDataSync();
  }

  ngOnDestroy() {
    if (this.rcDataSyncSubscription) {
      this.rcDataSyncSubscription.unsubscribe();
    }
  }

  private initializeRCDataSync() {
    if (this.isRCSuperAdmin()) {
      this.rcDataSyncSubscription = this.rcDataSyncService.templateUpdates$.subscribe(
        (event) => {
          console.log('[TemplatesComponent] RC data sync event received:', event);
          if (event.event === 'created' || event.event === 'updated' || event.event === 'deleted') {
            console.log('[TemplatesComponent] Refreshing templates due to RC sync event');
            this.loadTemplates();
          }
        }
      );
    }
  }

  loadTemplates() {
    this.isLoading = true;
    
    if (this.isSuperAdmin()) {
      // Load templates from all supervised MC companies
      this.rcOversightService.getMCTemplates().subscribe({
        next: (response: any) => {
          if (response.success) {
            this.templates = Array.isArray(response.data) ? response.data : [];
            this.applyFilters();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load MC templates:', error);
          this.isLoading = false;
        }
      });
    } else {
      // Load templates from user's own company (existing logic)
      this.communicationTemplateService.getTemplates().subscribe({
        next: (response) => {
          if (response.success) {
            this.templates = Array.isArray(response.data) ? response.data : [];
            this.applyFilters();
          }
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load templates:', error);
          this.isLoading = false;
        }
      });
    }
  }

  onChannelChange(channel: string) {
    this.selectedChannel = channel;
    this.applyFilters();
  }

  onSearch() {
    this.applyFilters();
  }

  applyFilters() {
    let filtered = [...this.templates];

    // Sort by creation date (latest first)
    filtered.sort((a, b) => {
      const dateA = new Date(a.created_at || 0).getTime();
      const dateB = new Date(b.created_at || 0).getTime();
      return dateB - dateA; // Descending order (latest first)
    });

    // Filter by channel
    if (this.selectedChannel !== 'all') {
      filtered = filtered.filter(template => 
        template.template_type === this.selectedChannel
      );
    }

    // Filter by search query
    if (this.searchQuery.trim()) {
      const query = this.searchQuery.toLowerCase();
      filtered = filtered.filter(template => 
        template.name.toLowerCase().includes(query) ||
        template.body.toLowerCase().includes(query) ||
        (template.subject && template.subject.toLowerCase().includes(query)) ||
        template.template_type.toLowerCase().includes(query)
      );
    }

    this.filteredTemplates = filtered;
  }

  openCreateModal() {
    this.selectedTemplate = null;
    this.isEditMode = false;
    this.showModal = true;
  }

  openEditModal(template: CommunicationTemplate) {
    this.selectedTemplate = template;
    this.isEditMode = true;
    this.showModal = true;
  }

  closeModal() {
    this.showModal = false;
    this.selectedTemplate = null;
  }

  onTemplateSave(template: CommunicationTemplate) {
    this.closeModal();
    // Reload templates to get complete data including company info
    setTimeout(() => {
      this.loadTemplates();
    }, 100); // Small delay to ensure modal closes first
  }

  copyTemplate(template: CommunicationTemplate) {
    const contentToCopy = template.subject 
      ? `${template.subject}\n\n${template.body}` 
      : template.body;
    
    navigator.clipboard.writeText(contentToCopy).then(() => {
      this.copiedTemplateId = template._id;
      setTimeout(() => {
        this.copiedTemplateId = null;
      }, 2000);
    }).catch(err => {
      console.error('Failed to copy template content:', err);
      const textArea = document.createElement('textarea');
      textArea.value = contentToCopy;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.copiedTemplateId = template._id;
      setTimeout(() => {
        this.copiedTemplateId = null;
      }, 2000);
    });
  }

  deleteTemplate(template: CommunicationTemplate) {
    this.templateToDelete = template;
    this.showDeleteModal = true;
  }

  confirmDelete() {
    if (this.templateToDelete) {
      this.communicationTemplateService.deleteTemplate(this.templateToDelete._id).subscribe({
        next: (response) => {
          if (response.success) {
            this.templates = this.templates.filter(t => t._id !== this.templateToDelete!._id);
            this.applyFilters();
            this.errorService.showInfo('Template deleted successfully', true, 3000);
          }
          this.closeDeleteModal();
        },
        error: (error) => {
          console.error('Failed to delete template:', error);
          this.errorService.handleHttpError(error, 'Failed to delete template. Please try again.');
          this.closeDeleteModal();
        }
      });
    }
  }

  cancelDelete() {
    this.closeDeleteModal();
  }

  closeDeleteModal() {
    this.showDeleteModal = false;
    this.templateToDelete = null;
  }

  getPreview(content: string): string {
    return content.length > 50 ? content.substring(0, 50) + '...' : content;
  }

  getChannelBadgeClass(templateType: string): string {
    switch (templateType) {
      case 'sms': return 'channel-badge sms';
      case 'email': return 'channel-badge email';
      case 'whatsapp': return 'channel-badge whatsapp';
      default: return 'channel-badge';
    }
  }

  getChannelDisplayName(templateType: string): string {
    switch (templateType) {
      case 'sms': return 'SMS';
      case 'email': return 'EMAIL';
      case 'whatsapp': return 'WHATSAPP';
      default: return templateType.toUpperCase();
    }
  }

  formatModifiedDate(date: Date | string): string {
    const d = new Date(date);
    const month = (d.getMonth() + 1).toString().padStart(2, '0');
    const day = d.getDate().toString().padStart(2, '0');
    const year = d.getFullYear();
    return `${month}/${day}/${year}`;
  }

  getRelativeTime(date: Date): string {
    const now = new Date();
    const templateDate = new Date(date);
    const diffInMs = now.getTime() - templateDate.getTime();
    const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60));
    const diffInDays = Math.floor(diffInHours / 24);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return `${diffInHours} hour${diffInHours > 1 ? 's' : ''} ago`;
    } else if (diffInDays < 7) {
      return `${diffInDays} day${diffInDays > 1 ? 's' : ''} ago`;
    } else {
      return templateDate.toLocaleDateString();
    }
  }

  // Permission check methods
  canManageTemplates(): boolean {
    const currentUser = this.authService.currentUserValue;
    return this.permissionService.canManageTemplates(currentUser?.role);
  }

  canAccessTemplates(): boolean {
    const currentUser = this.authService.currentUserValue;
    return this.permissionService.canAccessTemplates(currentUser?.role);
  }

  canEditTemplate(template: CommunicationTemplate): boolean {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser) return false;

    // Super admin can edit all templates
    if (currentUser.role === UserRole.OWNER) {
      return true;
    }

    // Admin can edit templates within their company
    if (currentUser.role === UserRole.ADMIN && 
        currentUser.company_id === template.company_id) {
      return true;
    }

    // Users can edit their own templates (but this shouldn't happen since users can't create templates)
    if (currentUser.sub === template.user_id) {
      return true;
    }

    return false;
  }

  canDeleteTemplate(template: CommunicationTemplate): boolean {
    return this.canEditTemplate(template);
  }


  isSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    const isRC = currentUser?.role === UserRole.OWNER && 
                 currentUser?.company_type === 'RC';
    console.log('[TemplatesComponent] isSuperAdmin check - Role:', currentUser?.role, 'CompanyType:', currentUser?.company_type, 'Result:', isRC);
    return isRC;
  }

  isRCSuperAdmin(): boolean {
    const currentUser = this.authService.currentUserValue;
    return currentUser?.role === UserRole.OWNER && currentUser?.company_type === 'RC';
  }
}